/**
 * User authorization helpers for messaging features
 *
 * These helpers determine user access levels for Direct Messages and other
 * messaging features based on user roles.
 */

import {
  INTERNAL_USER_ROLES,
  EXTERNAL_USER_ROLES,
  ADMIN_USER_ROLES,
  checkIfInternal,
  checkIfExternal,
  checkIfAdmin,
} from '@/src/lib/authorization/authorization';

/**
 * User data interface from Redux store
 */
export interface LoggedUser {
  data?: {
    id: number;
    userRole: number;
    role?: string;
    firstName?: string;
    lastName?: string;
    [key: string]: any;
  };
}

/**
 * Check if the logged-in user is an internal user
 * Internal users: MASTER_ADMIN, ADMIN, MANAGER, STAFF
 *
 * @param loggedUser - The logged user object from Redux store
 * @returns true if user is internal, false otherwise
 */
export const isInternalUser = (loggedUser: LoggedUser): boolean => {
  if (!loggedUser?.data) return false;
  return checkIfInternal(loggedUser.data);
};

/**
 * Check if the logged-in user is an external user
 * External users: MANAGER_GUEST, USER_GUEST
 *
 * @param loggedUser - The logged user object from Redux store
 * @returns true if user is external, false otherwise
 */
export const isExternalUser = (loggedUser: LoggedUser): boolean => {
  if (!loggedUser?.data) return false;
  return checkIfExternal(loggedUser.data);
};

/**
 * Check if the logged-in user is an admin
 * Admin users: MASTER_ADMIN, ADMIN
 *
 * @param loggedUser - The logged user object from Redux store
 * @returns true if user is admin, false otherwise
 */
export const isAdminUser = (loggedUser: LoggedUser): boolean => {
  if (!loggedUser?.data) return false;
  return checkIfAdmin(loggedUser.data);
};

/**
 * Check if the logged-in user can access Direct Messages
 * Only internal users can access DMs
 *
 * @param loggedUser - The logged user object from Redux store
 * @returns true if user can access DMs, false otherwise
 */
export const canAccessDirectMessages = (loggedUser: LoggedUser): boolean => {
  return isInternalUser(loggedUser);
};

/**
 * Check if the logged-in user can see admin-only features
 * Note: For DMs, admins cannot see DMs unless they are participants
 *
 * @param loggedUser - The logged user object from Redux store
 * @returns true if user is admin, false otherwise
 */
export const canSeeAdminFeatures = (loggedUser: LoggedUser): boolean => {
  return isAdminUser(loggedUser);
};

/**
 * Get user classification for messaging features
 *
 * @param loggedUser - The logged user object from Redux store
 * @returns Object with flags for internal, external, and admin status
 */
export const getUserMessagingPermissions = (loggedUser: LoggedUser) => {
  const isInternal = isInternalUser(loggedUser);
  const isExternal = isExternalUser(loggedUser);
  const isAdmin = isAdminUser(loggedUser);

  return {
    isInternal,
    isExternal,
    isAdmin,
    canAccessDMs: isInternal,
    canSeeAdminFeatures: isAdmin,
  };
};

/**
 * Check if user is a member of the channel based on members list
 *
 * @param userId - The user ID to check
 * @param channelMembers - Array of channel members with user-id property
 * @returns true if user is in the members list, false otherwise
 */
export const isUserChannelMember = (
  userId: number | undefined,
  channelMembers: Array<{ 'user-id': number }> | undefined
): boolean => {
  if (!userId || !channelMembers) return false;
  return channelMembers.some((member) => member['user-id'] === userId);
};

/**
 * Check if admin should see a channel even if not a member
 * Admins see all active channels EXCEPT DMs, even if not a member
 *
 * @param loggedUser - The logged user object from Redux store
 * @param channelType - The type of channel ('dm', 'client', 'internal')
 * @param channelStatus - The status of channel ('active', 'archived')
 * @param isMember - Whether the user is a member of the channel
 * @returns true if admin should see this channel, false otherwise
 */
export const shouldShowChannelToAdmin = (
  loggedUser: LoggedUser,
  channelType: string | undefined,
  channelStatus: string | undefined,
  isMember: boolean
): boolean => {
  const isAdmin = isAdminUser(loggedUser);

  // Non-admins only see channels they're members of
  if (!isAdmin) return isMember;

  // Admins cannot see DMs unless they're a member
  if (channelType === 'dm') return isMember;

  // Admins see all active non-DM channels (even if not a member)
  if (channelStatus === 'active') return true;

  // For archived channels, admins only see them if they're a member
  return isMember;
};

/**
 * Check if user should see unread badge for a channel
 * Hide unread badges for admin-only (non-member) channels
 *
 * @param loggedUser - The logged user object from Redux store
 * @param channelMembers - Array of channel members
 * @returns true if unread badge should be shown, false otherwise
 */
export const shouldShowUnreadBadge = (
  loggedUser: LoggedUser,
  channelMembers: Array<{ 'user-id': number }> | undefined
): boolean => {
  const userId = loggedUser?.data?.id;
  // Only show unread badge if user is a member
  return isUserChannelMember(userId, channelMembers);
};
